import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmModal } from '@microservices/wiskey-react-components'
import { DataGrid, GridCellParams, GridColDef } from '@microservices/wiskey-react-table-component'
import { Grid } from '@mui/material'

import {
  generateDataGridActionsColumnForStyleSettings,
  generateDataGridPreviewColumnsForStyleSettings,
} from '@pages/ConfigStyleSettings/helpers'

import { ExportActionsPanel } from '@components/ConfigActionsPanel'
import { UseEditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'

import { generateConfigSelectColumn } from '@helpers'
import { CONFIG_MENU_OPTION, FLEXIBLE } from '@constants'

type TableWithActionsProps = {
  columns: GridColDef<any>[]
  rows: readonly any[]
  onDelete?: (id: number) => void
  onEdit?: (id: number) => void
  loading?: boolean
  configMenu?: UseEditConfigMenu
}

export const TableWithActions: FC<TableWithActionsProps> = ({
  columns,
  rows,
  onDelete,
  onEdit,
  loading,
  configMenu,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [currentRowId, setCurrentRowId] = useState<number>()
  const { t } = useTranslation()

  const handleEdit = (params: GridCellParams<any, any>) => {
    onEdit?.(params.row.id)
  }

  const handleClickDelete = (params: GridCellParams<any, any>) => {
    if (params.row.id) {
      setShowDeleteModal(true)
      setCurrentRowId(params.row.id)
    }
  }

  const handleDelete = () => {
    if (currentRowId) {
      onDelete?.(currentRowId)
      setShowDeleteModal(false)
    }
  }

  const handleClose = () => setShowDeleteModal(false)

  const columnsWithActions = useMemo(
    () => [
      ...columns,
      ...generateDataGridPreviewColumnsForStyleSettings(),
      configMenu?.configMenuOption &&
        configMenu?.configMenuOption !== CONFIG_MENU_OPTION.IMPORT &&
        generateConfigSelectColumn({
          selectedConfigId: configMenu?.selectedConfigId,
          onSelectRow: configMenu?.handleSelectConfigId,
        }),
      generateDataGridActionsColumnForStyleSettings({
        onEdit: onEdit ? handleEdit : undefined,
        onDelete: onDelete ? handleClickDelete : undefined,
      }),
    ],
    [columns, configMenu?.selectedConfigId, configMenu?.configMenuOption]
  )

  return (
    <Grid container sx={{ ...FLEXIBLE, px: 1 }}>
      <EditConfigScriptDialog />
      {showDeleteModal && (
        <ConfirmModal
          actionBtnText={t('modal.delete.btn')}
          isShow={showDeleteModal}
          text={t('modal.delete.warning')}
          title={t('modal.delete.title')}
          containerStyle={{
            px: 0.5,
            py: 0.5,
            // borderRadius: 0
          }}
          onClose={handleClose}
          onConfirm={handleDelete}
        />
      )}
      <Grid container item sx={FLEXIBLE}>
        <DataGrid
          hideFooter
          columns={columnsWithActions}
          loading={loading}
          rows={rows}
          sx={{ borderRadius: 0 }}
        />
      </Grid>
      <ExportActionsPanel />
    </Grid>
  )
}
